import {
  type FeatureCollection,
  type GeoJsonObject,
  type MultiPolygon,
  type Point,
} from "geojson";
import { IMaybe } from "../util";

export enum BaseMap {
  Contries = "Contries",
  Satellite = "Satellite",
  Streets = "Streets",
  Terrain = "Terrain",
  Topo = "Topo",
  OpenStreetMap = "OpenStreetMap",
}

export interface BaseMapConfig {
  id: BaseMap;
  url: string;
  attribution: string;
  maxZoom?: number;
  minZoom?: number;
  active?: boolean;
}

interface DataSource {
  id: string;
  type: "geojson";
  data: GeoJsonObject;
}

export interface IndicatorProperties {
  id: string;
  year: number;
  value: number;
  percentile: number;
  additional_info: Record<string, string>;
}

// ----------------------------------------------

export interface IGeoData {
  id: number | string;
  longitude: number;
  latitude: number;
  health_facility_full_name: string;
  ward_id: string;
  front_view_photo_url?: string;
  ward_name?: string;
  lga_name?: string;
  state_name?: string;
  average_rating?: IMaybe<number>;
}
export interface IMortalityData {
  state: number | string;
  location_name: string;
  total_maternal_mortality: number;
}
export interface IBaseProperties {
  total_bhcpf_impact_funds: number;
  total_count: number;
  total_facility_functionality_1: number;
  total_facility_functionality_2: number;
  total_facility_functionality_partial: number;
  total_number_of_chos: number;
  total_number_of_jchews: number;
  total_number_of_lab_techs: number;
  total_number_of_medical_officers: number;
  total_number_of_nurses: number;
  total_number_of_pharmacy_techs: number;
  total_number_of_sba: number;
  total_number_of_schews: number;
  total_sba_gaps: number;
  functional_backup_power: number;
  staff_accommodation_availability: number;
}

export interface IDashboardPoint extends Partial<IBaseProperties> {}

export interface ICustomProperties extends IBaseProperties {
  crs: CRS;
  features: Feature[];
  type: string;
  geo_data: IGeoData[];
  mortality_data?: IMortalityData[];
}
export interface IBoundary {
  id: string | number;
  type: "geojson";
  max_value: number;
  data: ICustomProperties;
  // data: FeatureCollection<MultiPolygon | Point, Record<string, string | number | IGeoData[]>>;
}

export interface ICustomProperties2 {
  crs: CRS;
  features: Feature[];
  type: string;
  geo_data: IAssessor[];
}
export interface IMBoundary {
  id: string | number;
  type: "geojson";
  max_value: number;
  data: ICustomProperties2;
  // data: FeatureCollection<MultiPolygon | Point, Record<string, string | number | IGeoData[]>>;
}

export interface IAssessor {
  Name_assessor: string;
  "Designation (data collector)": string;
  "Phone Number (Data collector)": number;
  Zone: string;
  State: string;
  LGA: string;
  INEC_Ward: string;
  facilitygeolocation: string;
  latitude: number;
  longitude: number;
  altitude: number;
  precision: number;
}

// ----------------------------------------------

export interface PointsOfInterest
  extends DataSource,
    Pick<ICommonList, "name"> {
  color: string;
  data: FeatureCollection<Point, IndicatorProperties>;
}

export interface AreaOfInterest extends DataSource, Pick<ICommonList, "name"> {
  color: string;
  data: FeatureCollection<MultiPolygon, IndicatorProperties>;
}

export type IndicatorData = PointsOfInterest | AreaOfInterest;

export interface ICommonList {
  id: string;
  name: string;
}
export interface IState extends ICommonList {
  country_id: string;
}

export interface ILga extends ICommonList {
  state_id: string;
}

export interface IWard extends ICommonList {
  lga_id: string;
}
export interface IHF {
  id: number;
  ward_id: string;
  health_facility_full_name: string;
}

export interface ICategories extends ICommonList {
  subCategories: ISubCategories[];
}

export interface ISubCategories extends ICommonList {
  value: string;
  is_boolean: boolean;
  category_name: string;
}

export interface IndicatorDataset extends ICommonList {
  type: string;
  data: IndicatorDataType;
}

export interface IndicatorDataType extends IBaseProperties {
  type: string;
  crs: CRS;
  features: Feature[];
}

export interface IDashboardValues
  extends Omit<IndicatorDataType, "features" | "type" | "crs"> {}

type phc_count = {
  phc_count: FeatureProperties[];
};

export type IDashboardValuesPHC = IDashboardValues &
  phc_count & { geo_data?: IGeoData[] };

export interface IMapData
  extends Pick<IndicatorDataType, "type" | "crs" | "features"> {}

export interface CRS {
  type: string;
  properties: CRSProperties;
}

export interface CRSProperties {
  name: string;
}

export interface Feature {
  type: FeatureType;
  geometry: Geometry;
  properties: FeatureProperties;
}

export interface Geometry {
  type: GeometryType;
  coordinates: Array<Array<Array<number[]>>>;
}

export enum GeometryType {
  MultiPolygon = "MultiPolygon",
}

export interface FeatureProperties {
  health_facility_count: number;
  location_name: string;
  location_level: "zone" | "state" | "lga" | "ward";
  location_id: string;
  code?: IMaybe<string>;
  iso2?: IMaybe<string>;
  iso3?: IMaybe<string>;
  name?: IMaybe<string>;
  pk?: IMaybe<string>;
}

export enum FeatureType {
  Feature = "Feature",
}

export interface ILegendRange {
  [key: string]: [number, number];
}

export interface IPhc {
  id?: number;
  zone: string;
  state: string;
  lga: string;
  ward: string;
  latitude: number;
  longitude: number;
  altitude: number;
  precision: string;
  is_operational: boolean;
  type_of_assessment: string;
  original_facility_name: string;
  reason_for_replacement: string;
  health_facility_full_name: string;
  assessed_health_facility_name: string;
  alternate_facility_name: string;
  alternate_facility_name_details: string;
  facility_type: string;
  government_owned_building: boolean;
  building_owner: string;
  faith_based_organization_name: string;
  officer_in_charge: string;
  officer_gender: string;
  officer_phone_number: string;
  officer_email: string;
  facility_open_hours: string;
  facility_open_time: string;
  facility_close_time: string;
  has_ward_development_committee: boolean;
  wdc_recent_meeting: boolean;
  wdc_chairman_name: string;
  wdc_chairman_gender: string;
  wdc_chairman_phone_number: string;
  wdc_chairman_email: string;
  refer_for_complicated_labour: boolean;
  primary_referral_facility: string;
  referral_hospital_type: string;
  other_hospital_type: string;
  referral_hospital_lga: string;
  referral_hospital_state: string;
  referral_hospital_lga_select: string;
  usual_transport_for_referral: string;
  transport_keke_ambulance: boolean;
  transport_vehicle_ambulance: boolean;
  transport_private_car: boolean;
  transport_local_garage: boolean;
  transport_others: boolean;
  transport_other_specify: string;
  number_of_medical_officers: number;
  number_of_nurses: number;
  number_of_chos: number;
  number_of_jchews: number;
  number_of_schews: number;
  number_of_pharmacy_techs: number;
  number_of_lab_techs: number;
  number_of_medical_records_techs: number;
  number_of_health_attendants: number;
  number_of_cleaners: number;
  number_of_security_personnel: number;
  skilled_birth_attendants: string;
  sops_available: boolean;
  facility_funding: string;
  bhcpf_impact_funds: string;
  bhcpf_impact_funds_other: string;
  impact_funding_frequency: string;
  impact_funding_frequency_other: string;
  bhcpf_funding_frequency: string;
  bhcpf_funding_frequency_other: string;
  security_infrastructure: string;
  perimeter_fence: boolean;
  gate: boolean;
  front_view_photo: string;
  front_view_photo_url: string;
  side_view_photo: string;
  side_view_photo_url: string;
  sign_post_photo: string;
  sign_post_photo_url: string;
  reception: number;
  reception_condition: string;
  reception_photo: string;
  reception_photo_url: string;
  consulting_rooms: number;
  consulting_rooms_condition: string;
  consulting_rooms_photo: string;
  consulting_rooms_photo_url: string;
  delivery_room: number;
  delivery_room_condition: string;
  delivery_room_photo: string;
  delivery_room_photo_url: string;
  inpatient_ward: number;
  inpatient_ward_condition: string;
  inpatient_ward_photo: string;
  inpatient_ward_photo_url: string;
  laboratory: number;
  laboratory_condition: string;
  laboratory_photo: string;
  laboratory_photo_url: null;
  pharmacy: number;
  pharmacy_condition: string;
  pharmacy_photo: string;
  pharmacy_photo_url: null;
  treatment_room: number;
  treatment_room_condition: string;
  treatment_room_photo: string;
  treatment_room_photo_url: null;
  toilets: number;
  toilets_condition: string;
  toilets_photo: string;
  toilets_photo_url: string;
  available_toilets_type: string;
  roof_condition: string;
  roof_type: string;
  ceiling_photo: string;
  ceiling_photo_url: string;
  wall_condition: string;
  floor_condition: string;
  entrance_exit_doors_condition: string;
  windows_condition: string;
  medical_records_space: boolean;
  disabled_access: boolean;
  staff_accommodation: boolean;
  staff_accommodation_within_premises: null;
  staff_accommodation_in_use: null;
  staff_accommodation_bedrooms: string;
  main_power_source: string;
  other_power_source: string;
  solar_system_functional: null;
  generator_functional: null;
  backup_power_source: string;
  backup_power_source_national_grid: boolean;
  backup_power_source_generator: boolean;
  backup_power_source_solar: boolean;
  backup_power_source_none: boolean;
  backup_power_source_other: boolean;
  backup_solar_functional: null;
  backup_generator_functional: boolean;
  backup_power_source_other_specify: string;
  medical_waste_disposal: string;
  waste_disposal_incinerator: boolean;
  waste_disposal_protected_pit: boolean;
  waste_disposal_lga_collection: boolean;
  waste_disposal_other: boolean;
  medical_waste_disposal_other: string;
  main_water_source: string;
  main_water_source_other: string;
  handwashing_facilities: string;
  handwashing_facilities_veronica_buckets: boolean;
  handwashing_facilities_other: string;
  handwashing_facilities_other_specify: string;
  services_offered: string;
  antenatal_care: boolean;
  labour_delivery: boolean;
  family_planning: boolean;
  routine_immunization: boolean;
  nutrition: boolean;
  growth_monitoring: boolean;
  treatment_cough_fever_diarrhea: boolean;
  visual_acuity: boolean;
  screening_ncds: boolean;
  antenatal_clients_last_month: number;
  delivery_clients_last_month: number;
  family_planning_clients_last_month: number;
  routine_immunization_clients_last_month: number;
  growth_monitoring_clients_last_month: number;
  malaria_testing_clients_last_month: number;
  diarrhea_treated_clients_last_month: number;
  ari_treated_clients_last_month: number;
  diabetes_screened_clients_last_month: number;
  hypertension_screened_clients_last_month: number;
  visual_acuity_screened_clients_last_month: number;
  conducts_routine_immunization: boolean;
  conducts_outreach_immunization: boolean;
  administers_uterotonics: boolean;
  administers_injectable_antibiotics: boolean;
  administers_injectable_anticonvulsants: boolean;
  screens_for_conditions: null;
  malaria_screening: boolean;
  tuberculosis_screening: boolean;
  hiv_screening: boolean;
  sti_screening: boolean;
  diabetes_screening: boolean;
  tb_testing_clients_last_month: number;
  rvs_clients_last_month: number;
  sti_clients_last_month: number;
  diabetes_clients_last_month: number;
  tb_drugs_given: null;
  offers_health_education: boolean;
  comments: null;
  number_of_sbas: number;
  sba_gaps: number;
  main_power_source_summary: string;
  functional_backup_power: boolean;
  access_to_power_combinations: string;
  staff_accommodation_availability: boolean;
  need_for_major_repairs: boolean;
  facility_functionality: string;
  solarization_need: string;
  sba_need: string;
  staff_accommodation_need: string;
  physical_upgrade_needed: string;
  alternative_power_source_needed: string;
  no_need: string;
  need_repair: string;
  pk: string;
  zone_name?: string;
  state_name?: string;
  lga_name?: string;
  ward_name?: string;
  revitalizations: IMaybe<Record<string, string>[]>;
  average_rating: number;
  feedback_count: number;
}

export type QueryType = {
  [key: string]: boolean | number;
};

export interface IQueriedPhc {
  id: number;
  health_facility_full_name: string;
  ward_id: string;
  ward_name: string;
  lga_name: string;
  state_name: string;
  zone_name: string;
  country_name: string;
}

export interface IPhcQuery {
  count: number;
  data: IQueriedPhc[];
}

export interface IRevitalization {
  id: string;
  created_by: IByUser;
  updated_by: IByUser;
  funding_source: FundingSource;
  phc: Phc;
  previous_facility_image: string;
  current_facility_image: string;
  accommodation_image: null;
  borehole_image: null;
  solar_power_image: null;
  equipment_image: null;
  name: string;
  boq_cleared: string;
  contract_awarded: string;
  commenced_revitalization: string;
  expected_completion_date: string;
  is_revitalization_completed: string;
  actual_completion_date: string;
  main_phc: string;
  has_accommodation: string;
  is_rehabilitation: string;
  has_borehole: string;
  has_solar_power: string;
  has_equipment: string;
  approval_status: string;
  remarks: string;
  status: boolean;
  created_at: string;
  updated_at: string;
}

export interface IByUser {
  id: number;
  username: string;
  email: string;
  name: string;
  assigned_state_name: IMaybe<string>;
}

export interface FundingSource {
  id: string;
  name: string;
  description: string;
}

export interface Phc {
  id: number;
  health_facility_full_name: string;
  zone: string;
  zone_name: string;
  state: string;
  state_name: string;
  lga: string;
  lga_name: string;
  ward: string;
  ward_name: string;
  longitude: number;
  latitude: number;
  data_source: string;
  front_view_photo_url: string;
  side_view_photo_url: string;
  facility_functionality: string;
}
